import React, { memo, useMemo } from 'react';
import '../styles/float-animation.css';

interface Review {
  title: string;
  author: string;
  time: string;
  text: string;
}

interface ReviewCardProps {
  review: Review;
  id?: string; // Für spezifische IDs wie "left-card" oder "right-card"
  className?: string;
  isTransitioning?: boolean;
  isVisible?: boolean; // Neue Prop für Loading-State
}

// Hilfsfunktion für zufällige Y-Position
const getRandomYPosition = (cardId: string) => {
  // Verwende cardId als Seed für konsistente, aber verschiedene Positionen
  const seed = cardId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const random = (seed * 9301 + 49297) % 233280 / 233280; // Simple pseudo-random
  
  // Y-Position zwischen 20% und 80% der Viewport-Höhe
  const minPercent = 20;
  const maxPercent = 80;
  return minPercent + (random * (maxPercent - minPercent));
};

const ReviewCard: React.FC<ReviewCardProps> = memo(({ 
  review, 
  id,
  className = '',
  isTransitioning = false,
  isVisible = true
}) => {
  // Memoized zufällige Y-Position basierend nur auf ID (nicht author!)
  const randomYPosition = useMemo(() => {
    if (!id) return 50; // Fallback
    return getRandomYPosition(id); // Nur ID verwenden, nicht author!
  }, [id]); // author entfernt aus Dependencies!

  // Berechne eine eindeutige Animation-Delay und Variation basierend auf der ID
  const animationConfig = useMemo(() => {
    if (!id) return { delay: 0, variant: '' };
    
    const seed = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const delay = (seed % 3000) / 1000; // 0-3 Sekunden Delay
    
    // Verschiedene Float-Variationen
    const variants = ['', 'float-slow', 'float-fast'];
    const variant = variants[seed % variants.length];
    
    return { delay, variant };
  }, [id]);

  // Memoized Styles mit schwebendem Effekt und Loading-State
  const cardStyle: React.CSSProperties = useMemo(() => {
    // Angepasste Transition für problematischen Übergang
    const transitionDuration = '0.6s';
    const transitionCurve = 'cubic-bezier(0.4, 0, 0.2, 1)';
    
    // Loading-State: Karte ist nicht sichtbar
    if (!isVisible) {
      return {
        opacity: 0,
        transform: 'translate3d(0, 30px, 0) scale(0.9)',
        transition: `opacity 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
        backfaceVisibility: 'hidden' as const,
        WebkitBackfaceVisibility: 'hidden' as const,
        WebkitFontSmoothing: 'antialiased' as const,
        MozOsxFontSmoothing: 'grayscale' as const,
        willChange: 'opacity, transform',
        zIndex: 10,
        top: `${randomYPosition}%`,
        animationDelay: `${animationConfig.delay}s`
      };
    }
    
    return {
      opacity: isTransitioning ? 0 : 1,
      transform: isTransitioning ? 'translate3d(0, 20px, 0) scale(0.95)' : 'translate3d(0, 0, 0) scale(1)',
      transition: `opacity ${transitionDuration} ${transitionCurve}, transform ${transitionDuration} ${transitionCurve}`,
      backfaceVisibility: 'hidden' as const,
      WebkitBackfaceVisibility: 'hidden' as const,
      WebkitFontSmoothing: 'antialiased' as const,
      MozOsxFontSmoothing: 'grayscale' as const,
      willChange: 'opacity, transform',
      zIndex: 10,
      top: `${randomYPosition}%`,
      // Animation wird über CSS-Klasse gesteuert
      animationDelay: `${animationConfig.delay}s`
    };
  }, [isTransitioning, isVisible, randomYPosition, animationConfig.delay]);

  // Memoized Sterne für bessere Performance - exakt wie im HTML
  const starsArray = useMemo(() => Array.from({ length: 5 }, (_, i) => (
    <img 
      key={i}
      src="/assets/images/star.svg" 
      alt="Stern"
    />
  )), []);

  return (
    <div 
      className={`review-card ${animationConfig.variant} ${className}`.trim()}
      id={id}
      style={cardStyle}
    >
      <h3>{review.title}</h3>
      <div className="middle">
        <div className="stars-container">
          {starsArray}
        </div>
        <time>{review.time}</time>
      </div>
      <p>{review.text}</p>
    </div>
  );
});

ReviewCard.displayName = 'ReviewCard';

export default ReviewCard;
